/**
 * SF-T-164: a pull request that changes what people see should show it, with before and after
 * screenshots (or a recording) in its description. The hub marks a task's open PR when it changes such
 * files and its description has no image or video, and the PR's row on the task says so.
 *
 * Which files people see is worked out from the file name alone, so it holds for every project: markup,
 * components and styles, but not their tests or stories. A project can set its own (`SeenFilesConfig`).
 */

/** Extensions of files that change what people see. */
const VISIBLE_EXTENSIONS = new Set(['tsx', 'jsx', 'vue', 'svelte', 'astro', 'html', 'htm', 'css', 'scss', 'sass', 'less', 'styl']);

/** Tests, stories and snapshots: changed with UI, but not themselves seen. */
const NOT_SEEN = [/\.(test|spec|stories|story)\.[a-z]+$/i, /(^|\/)(__tests__|__snapshots__|__mocks__|e2e|tests?)\//i];

/**
 * A project's own rule, from `prScreenshots` in its devkit.config.json: `seen`, globs of the files people
 * see (instead of the extensions above), and `notSeen`, globs of files that never count (added to the
 * tests and stories left out). Globs match the whole repo path: `*` within a folder, `**` across folders.
 */
export interface SeenFilesConfig {
  seen: RegExp[];
  notSeen: RegExp[];
}

/** Most globs taken from each list. */
const MAX_GLOBS = 50;

/** A glob as a regular expression over a whole repo path. */
export function globToRegExp(glob: string): RegExp {
  let out = '';
  for (let i = 0; i < glob.length; i++) {
    const c = glob[i];
    if (c === '*' && glob[i + 1] === '*') {
      i += 1;
      if (glob[i + 1] === '/') {
        i += 1;
        out += '(?:.*/)?';
      } else out += '.*';
    } else if (c === '*') out += '[^/]*';
    else if (c === '?') out += '[^/]';
    else out += c.replace(/[.+^${}()|[\]\\]/g, '\\$&');
  }
  return new RegExp(`^${out}$`, 'i');
}

const globs = (value: unknown): RegExp[] =>
  Array.isArray(value)
    ? value
        .filter((g): g is string => typeof g === 'string' && !!g.trim())
        .slice(0, MAX_GLOBS)
        .map((g) => globToRegExp(g.trim().replace(/^\.?\//, '')))
    : [];

/** The rule from a parsed devkit.config.json; null when it has none (the extensions above apply). */
export function seenFilesConfig(devkitConfig: unknown): SeenFilesConfig | null {
  const block = devkitConfig && typeof devkitConfig === 'object' ? (devkitConfig as { prScreenshots?: unknown }).prScreenshots : undefined;
  if (!block || typeof block !== 'object') return null;
  const { seen, notSeen } = block as { seen?: unknown; notSeen?: unknown };
  const config = { seen: globs(seen), notSeen: globs(notSeen) };
  return config.seen.length || config.notSeen.length ? config : null;
}

/** Whether a changed file (a repo path) is one people see, by the project's rule when it has one. */
export function isVisibleFile(path: string, config: SeenFilesConfig | null = null): boolean {
  if (NOT_SEEN.some((re) => re.test(path))) return false;
  if (config?.notSeen.some((re) => re.test(path))) return false;
  if (config?.seen.length) return config.seen.some((re) => re.test(path));
  const ext = /\.([a-z0-9]+)$/i.exec(path)?.[1]?.toLowerCase();
  return !!ext && VISIBLE_EXTENSIONS.has(ext);
}

/**
 * How many pictures (images and videos) GitHub's rendering of a description has, wherever they're hosted:
 * uploads, files in the repo, other hosts (camo links). Emoji and avatars don't count.
 */
export function picturesInHtml(html: string): number {
  let count = 0;
  for (const [tag] of html.matchAll(/<(?:img|video)\b[^>]*>/gi)) {
    if (/\sclass="[^"]*\bemoji\b/i.test(tag)) continue;
    const src = /\ssrc="([^"]*)"/i.exec(tag)?.[1] ?? '';
    if (!src || /^https:\/\/avatars\.githubusercontent\.com\//i.test(src)) continue;
    count += 1;
  }
  return count;
}

/** How many of the visible files the note names. */
export const NO_PICTURES_SAMPLE = 5;

/** The note on a task's PR: it changes files people see, and its description has no pictures. */
export interface TaskPrNoPictures {
  /** How many changed files people see. */
  files: number;
  /** The first few of them. */
  sample: string[];
  /** The PR's head commit when it was read. */
  head_sha: string;
  /** When it was read. */
  at: string;
}

/**
 * The note for a PR from its changed files and how many images or videos its description has: null when
 * it changes nothing people see, or shows it already.
 */
export function noPicturesNote(files: string[], media: number, headSha: string, at: string, config: SeenFilesConfig | null = null): TaskPrNoPictures | null {
  if (media > 0) return null;
  const visible = files.filter((f) => isVisibleFile(f, config));
  if (!visible.length) return null;
  return { files: visible.length, sample: visible.slice(0, NO_PICTURES_SAMPLE), head_sha: headSha, at };
}

/** The hint on the PR's badge. */
export function noPicturesHint(note: TaskPrNoPictures): string {
  const more = note.files > note.sample.length ? `, and ${note.files - note.sample.length} more` : '';
  const what = note.files === 1 ? 'a file people see' : `${note.files} files people see`;
  return `It changes ${what} (${note.sample.join(', ')}${more}), but its description has no screenshots or recordings. Attach before and after shots to the PR (gh pr edit --attach).`;
}
